import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiLoaderModule,
} from '@taiga-ui/core';
import { tuiIsFalsy, TuiLetModule } from '@taiga-ui/cdk';
import { TuiTableModule } from '@taiga-ui/addon-table';
import {
  catchError,
  EMPTY,
  map,
  merge,
  Observable,
  share,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { InvoiceService } from '@shared/services/invoice.service';
import { SpaceNumberPipe } from '@shared/pipes/space-number.pipe';
import { AlertService } from '@shared/services/alert.service';
import { InvoiceParseDialogComponent } from '@shared/components/invoice/invoice-parse-dialog/invoice-parse-dialog.component';
import { DocumentVisibilityService } from '@shared/services/document-visibility.service';
import { InvoiceDto, ResolutionStrategy } from '../../../../types/invoice';

@Component({
  selector: 'app-invoices-journal',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    TuiButtonModule,
    TuiLetModule,
    TuiLoaderModule,
    TuiTableModule,
    SpaceNumberPipe,
  ],
  templateUrl: './invoices-journal.component.html',
  styleUrl: './invoices-journal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoicesJournalComponent implements OnInit {
  openDialog$ = new Subject<void | InvoiceDto>();
  fetch$ = new Subject<void>();

  getSourceFile$ = new Subject<number>();

  tableHeaders: Array<{
    name: string;
    value: keyof InvoiceDto;
  }> = [
    { name: 'ID', value: 'id' },
    { name: 'Номер', value: 'number' },
    { name: 'Дата', value: 'date' },
    { name: 'Продавец', value: 'seller' },
    { name: 'Покупатель', value: 'buyer' },
    { name: 'Стоимость', value: 'cost' },
  ];

  readonly request$ = merge(
    this.fetch$,
    this.documentVisibilityService.visible$.pipe(startWith(null)),
  ).pipe(
    switchMap(() =>
      this.invoiceService.getAllInvoices({
        resolutionStrategy: ResolutionStrategy.FULL,
      }),
    ),
    share(),
    takeUntilDestroyed(this.destroyRef),
  );

  readonly items$: Observable<InvoiceDto[]> = this.request$.pipe(startWith([]));

  readonly loading$ = this.request$.pipe(map(tuiIsFalsy));

  constructor(
    private readonly invoiceService: InvoiceService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly alert: AlertService,
    private readonly documentVisibilityService: DocumentVisibilityService,
  ) {}

  onOpenDialog() {
    this.openDialog$
      .pipe(
        switchMap(invoice =>
          this.dialogs.open(
            new PolymorpheusComponent(InvoiceParseDialogComponent),
            {
              size: 'page',
              dismissible: false,
              closeable: false,
              data: { invoice },
            },
          ),
        ),
        tap(() => this.fetch$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onGetSourceFile() {
    return this.getSourceFile$
      .pipe(
        switchMap(id =>
          this.invoiceService.getSourceFile(id).pipe(
            catchError(err => {
              this.alert.showError(err.error);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  get columns() {
    return [...this.tableHeaders.map(header => header.value), 'actions'];
  }

  ngOnInit() {
    this.onOpenDialog();
    this.onGetSourceFile();
  }
}
